import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../shared/Nav";
import Footer from "../shared/Footer";
import "../assets/css/variables.css";
import "../assets/css/global.css";
import "../assets/css/Index.css";
import "../assets/css/pages.css";
import clinicVideo from "../assets/images/clinic_video.mov";

const API_URL = `${process.env.REACT_APP_API_URL}/api`;

// ── Count-up hook ──────────────────────────────────────────────────────────
function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!target || !started) return;
    const num = parseFloat(target);
    if (isNaN(num)) return;

    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * num;
      setCount(num < 10 ? parseFloat(current.toFixed(1)) : Math.floor(current));
      if (progress < 1) requestAnimationFrame(tick);
      else setCount(num);
    };
    requestAnimationFrame(tick);
  }, [target, started, duration]);

  const num = parseFloat(target);
  const suffix = target?.toString().replace(/[\d.]/g, "") || "";
  const display = isNaN(num) ? target : `${count}${suffix}`;

  return { display, trigger: () => setStarted(true) };
}

// ── Animated Hero Stat ─────────────────────────────────────────────────────
function AnimatedStat({ value, label }) {
  const { display, trigger } = useCountUp(value, 2000);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) trigger();
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [trigger]);

  return (
    <div ref={ref} className="stat-card">
      <div className="stat-card__num">{display}</div>
      <div className="stat-card__lbl">{label}</div>
    </div>
  );
}

// ── Animated About Num ─────────────────────────────────────────────────────
function AnimatedAboutNum({ value, label }) {
  const { display, trigger } = useCountUp(value, 2000);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) trigger();
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [trigger]);

  return (
    <div ref={ref} className="about-num">
      <div className="about-num__n">{display}</div>
      <div className="about-num__l">{label}</div>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────
function Modal({ modal, onClose, onContact }) {
  if (!modal) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>

        {modal.type === "news" &&
          (() => {
            const d = modal.data;

            // Normalize tag
            const tag = Array.isArray(d.tag)
              ? d.tag[0]
              : typeof d.tag === "string"
                ? d.tag.split(",")[0].trim()
                : "News";

            // All tags as array for display
            const allTags = Array.isArray(d.tag)
              ? d.tag
              : typeof d.tag === "string"
                ? d.tag
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean)
                : [];

            // Format date
            const rawDate = d.date || d.createdAt || d.publishedAt;
            const formattedDate = rawDate
              ? new Date(rawDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : "";

            // Body fallback
            const bodyText =
              d.body ||
              d.content ||
              d.text ||
              d.article ||
              d.details ||
              d.fullText ||
              d.summary ||
              d.description ||
              d.excerpt ||
              "";

            // Image fallback
            const imgSrc =
              d.image ||
              d.imageUrl ||
              d.img ||
              d.thumbnail ||
              d.coverImage ||
              null;

            return (
              <>
                {/* Image or icon banner */}
                {imgSrc ? (
                  <div
                    style={{
                      width: "100%",
                      height: 180,
                      borderRadius: 12,
                      overflow: "hidden",
                      marginBottom: 16,
                    }}
                  >
                    <img
                      src={imgSrc}
                      alt={d.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: 44,
                      marginBottom: 14,
                      textAlign: "center",
                    }}
                  >
                    {d.icon || "📰"}
                  </div>
                )}

                {/* Tags row */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: 10,
                  }}
                >
                  {allTags.length > 0 ? (
                    allTags.map((t, i) => (
                      <span key={i} className="m-tag" style={{ fontSize: 11 }}>
                        {t}
                      </span>
                    ))
                  ) : (
                    <span className="m-tag">{tag}</span>
                  )}
                </div>

                <h2
                  style={{
                    fontFamily: "'Cormorant Garamond',serif",
                    fontSize: 24,
                    color: "#0B1F3A",
                    margin: "10px 0 8px",
                  }}
                >
                  {d.title}
                </h2>

                {bodyText ? (
                  <p
                    style={{
                      color: "#5E6E82",
                      fontSize: 14.5,
                      lineHeight: 1.75,
                    }}
                  >
                    {bodyText}
                  </p>
                ) : (
                  <p
                    style={{
                      color: "#aab4bf",
                      fontSize: 13.5,
                      fontStyle: "italic",
                    }}
                  >
                    No content available for this article.
                  </p>
                )}

                {formattedDate && (
                  <p
                    style={{
                      color: "#1B8FA6",
                      fontSize: 12.5,
                      marginTop: 14,
                      fontWeight: 600,
                    }}
                  >
                    📅 {formattedDate}
                  </p>
                )}
              </>
            );
          })()}

        {modal.type === "solution" && (
          <>
            <div style={{ fontSize: 38, marginBottom: 10 }}>
              {modal.data.icon}
            </div>
            <span className="m-tag">Solution</span>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond',serif",
                fontSize: 24,
                color: "#0B1F3A",
                margin: "10px 0 8px",
              }}
            >
              {modal.data.title}
            </h2>
            <p style={{ color: "#5E6E82", lineHeight: 1.78, marginTop: 12 }}>
              {modal.data.desc}
            </p>
            <div style={{ marginTop: 22 }}>
              <button
                className="btn-primary"
                onClick={() => {
                  onClose();
                  onContact();
                }}
              >
                Get Started →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────
function Skeleton({ width = "100%", height = 20, radius = 6, style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.4s infinite",
        ...style,
      }}
    />
  );
}

// ── Helper: safely extract array from various API response shapes ───────────
function extractArray(data, ...keys) {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function Index() {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);

  const [news, setNews] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    trials: "30+",
    clinicians: "250+",
    years: "1.5",
    states: "6+",
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const endpoints = [
          "/news",
          "/solutions",
          "/expertise",
          "/locations",
          "/about",
        ];

        const results = await Promise.all(
          endpoints.map(async (ep) => {
            const res = await fetch(`${API_URL}${ep}`);
            const data = await res.json();
            console.log(`✅ ${ep}:`, data);
            return { ep, data };
          }),
        );

        results.forEach(({ ep, data }) => {
          if (ep === "/news") {
            const arr = extractArray(data, "news", "data");
            setNews(arr);
            console.log("📰 Parsed news:", arr);
          }

          if (ep === "/solutions") {
            const arr = extractArray(data, "solutions", "data");
            setSolutions(arr);
            console.log("💡 Parsed solutions:", arr);
          }

          if (ep === "/expertise") {
            const arr = extractArray(data, "expertise", "data");
            setExpertise(arr);
            console.log("🔬 Parsed expertise:", arr);
          }

          if (ep === "/locations") {
            const arr = extractArray(data, "locations", "data");
            setLocations(arr);
            console.log("📍 Parsed locations:", arr);
          }

          if (ep === "/about") {
            const aboutStats = data?.data?.stats || [];
            const find = (keyword) =>
              aboutStats.find((s) => s.label?.toLowerCase().includes(keyword))
                ?.value || null;
            setStats({
              trials: find("trial") || find("completed") || "30+",
              clinicians: find("clinician") || find("network") || "250+",
              years: find("year") || find("age") || "1.5",
              states: find("state") || find("covered") || "6+",
            });
          }
        });
      } catch (err) {
        console.error("❌ Fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const goTo = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <Nav />

      {/* ── HERO ── */}
      <section className="hero">
        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.4,
          }}
        >
          <source src={clinicVideo} type="video/mp4" />
        </video>
        <div className="hero__bg" />
        <div className="hero__grid" />
        <div className="hero__content">
          <div className="hero__badge fu">
            India's Premier Site Management Organization
          </div>
          <h1 className="fu2">
            Welcome to
            <br />
            <em>Accelia Clinical</em>
            <br />
            Solutions
          </h1>
          <p className="fu3">
            Your trusted partner in global clinical trials and biopharmaceutical
            innovation — delivering precision, speed, and compliance across
            every therapeutic area.
          </p>
          <div className="hero__actions fu3">
            <button className="btn-primary" onClick={() => goTo("/solutions")}>
              Explore Our Solutions →
            </button>
            <button className="btn-outline" onClick={() => goTo("/contact")}>
              Get in Touch
            </button>
          </div>
        </div>

        {/* Animated Hero Stats */}
        <div className="hero__stats">
          {[
            [stats.trials, "Completed Trials"],
            [stats.clinicians, "Clinician Network"],
            [stats.years, "Years of Excellence"],
            [stats.states, "States Covered"],
          ].map(([n, l], i) => (
            <AnimatedStat key={i} value={n} label={l} />
          ))}
        </div>
      </section>

      {/* ── NEWS ── */}
      <section style={{ background: "#F2F7FA" }}>
        <div style={{ textAlign: "center" }}>
          <div className="sec-tag" style={{ display: "inline-block" }}>
            News & Resources
          </div>
          <h2 className="sec-title">Latest Insights from Accelia</h2>
          <p className="sec-sub">
            Access curated insights, clinical updates, and thought leadership
            from Accelia — empowering informed decisions in research.
          </p>
        </div>
        <div className="news-grid">
          {loading
            ? [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="news-card"
                  style={{
                    padding: 16,
                    gap: 12,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Skeleton height={120} radius={10} />
                  <Skeleton width="60%" height={14} />
                  <Skeleton height={16} />
                  <Skeleton width="80%" height={14} />
                  <Skeleton width="40%" height={12} />
                </div>
              ))
            : news.slice(0, 3).map((n, i) => {
                const rawTag = Array.isArray(n.tag)
                  ? n.tag[0]
                  : typeof n.tag === "string"
                    ? n.tag.split(",")[0].trim()
                    : "News";

                const rawDate = n.date || n.createdAt || n.publishedAt;
                const formattedDate = rawDate
                  ? new Date(rawDate).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "";

                const bodyText =
                  n.body ||
                  n.content ||
                  n.text ||
                  n.article ||
                  n.details ||
                  n.fullText ||
                  n.summary ||
                  n.description ||
                  n.excerpt ||
                  "";

                // Resolve image: try common API field names
                const imgSrc =
                  n.image ||
                  n.imageUrl ||
                  n.img ||
                  n.thumbnail ||
                  n.coverImage ||
                  n.cover ||
                  n.photo ||
                  null;

                return (
                  <div
                    key={n._id || i}
                    className="news-card"
                    onClick={() => setModal({ type: "news", data: n })}
                  >
                    {/* ── Card Image ── */}
                    <div
                      className="news-card__img"
                      style={{
                        background:
                          n.bg ||
                          "linear-gradient(135deg, #e8f4f8 0%, #c8e6f0 100%)",
                        position: "relative",
                        overflow: "hidden",
                        minHeight: 160,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={n.title}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      {/* Fallback icon — shown when no image or image fails */}
                      <div
                        style={{
                          display: imgSrc ? "none" : "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          width: "100%",
                          height: "100%",
                          minHeight: 160,
                          background:
                            "linear-gradient(135deg, #1B8FA6 0%, #0B1F3A 100%)",
                        }}
                      >
                        <span style={{ fontSize: 42 }}>{n.icon || "📰"}</span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            color: "rgba(255,255,255,0.6)",
                            textTransform: "uppercase",
                          }}
                        >
                          {rawTag}
                        </span>
                      </div>
                    </div>

                    <div className="news-card__body">
                      <span className="news-tag">{rawTag}</span>
                      <h3>{n.title}</h3>
                      {bodyText && (
                        <p>
                          {bodyText.substring(0, 110)}
                          {bodyText.length > 110 ? "\u2026" : ""}
                        </p>
                      )}
                      {formattedDate && (
                        <div className="news-card__date">{formattedDate}</div>
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <button
            className="btn-primary"
            style={{ background: "#1B8FA6" }}
            onClick={() => goTo("/news")}
          >
            View All News & Events
          </button>
        </div>
      </section>

      {/* ── SOLUTIONS ── */}
      <section>
        <div style={{ textAlign: "center" }}>
          <div className="sec-tag" style={{ display: "inline-block" }}>
            Our Solutions
          </div>
          <h2 className="sec-title">Integrated SMO Excellence</h2>
          <p className="sec-sub">
            From site-level execution to regulatory excellence, we offer
            integrated solutions that drive precision, quality, and accelerated
            clinical outcomes.
          </p>
        </div>
        <div className="solutions-grid">
          {loading
            ? [1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="sol-card"
                  style={{ gap: 10, display: "flex", flexDirection: "column" }}
                >
                  <Skeleton width={40} height={40} radius={10} />
                  <Skeleton width="70%" height={16} />
                  <Skeleton height={14} />
                  <Skeleton width="85%" height={14} />
                </div>
              ))
            : solutions.slice(0, 6).map((s, i) => (
                <div
                  key={s._id || i}
                  className="sol-card"
                  onClick={() => setModal({ type: "solution", data: s })}
                >
                  <div className="sol-card__icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <div className="sol-card__km">Know More →</div>
                </div>
              ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <button
            className="btn-primary"
            style={{ background: "#1B8FA6" }}
            onClick={() => goTo("/solutions")}
          >
            View All Solutions
          </button>
        </div>
      </section>

      {/* ── EXPERTISE ── */}
      <section style={{ background: "#0B1F3A" }}>
        <div className="sec-tag sec-tag--gold">Therapeutic Expertise</div>
        <h2 className="sec-title sec-title--white">
          Across Every Critical Therapy Area
        </h2>
        <p
          style={{
            color: "rgba(255,255,255,0.65)",
            fontSize: 16,
            lineHeight: 1.75,
            maxWidth: 620,
            marginBottom: 28,
          }}
        >
          With a strong SMO foundation, we bring therapeutic excellence across
          Oncology, Hematology, Gastroenterology, Neurology, Orthopedics,
          Ophthalmology, Pediatrics, Vaccines, Infectious Diseases, Rare
          Diseases, Cardiology, Gynaecology, and General Medicine.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {loading
            ? [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Skeleton key={i} width={100} height={34} radius={999} />
              ))
            : expertise.slice(0, 13).map((e, i) => (
                <button
                  key={e._id || i}
                  className="expertise-chip"
                  onClick={() => goTo("/expertise")}
                >
                  {e.title}
                </button>
              ))}
        </div>
        <div style={{ marginTop: 36 }}>
          <button className="btn-outline" onClick={() => goTo("/expertise")}>
            Explore All Expertise Areas
          </button>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section>
        <div className="about-grid">
          <div>
            <div className="sec-tag">About Accelia</div>
            <h2 className="sec-title">
              Benchmark in Speed, Precision & Site Excellence
            </h2>
            <p
              style={{
                color: "#5E6E82",
                fontSize: 16,
                lineHeight: 1.82,
                marginBottom: 18,
              }}
            >
              Delivering 30+ clinical trials in just 1.5 years, Accelia Clinical
              Solutions sets the benchmark in speed, precision, and site
              excellence — consistently surpassing sponsor timelines and ranking
              among India's top recruiters.
            </p>
            <p
              style={{
                color: "#5E6E82",
                fontSize: 16,
                lineHeight: 1.82,
                marginBottom: 30,
              }}
            >
              Powered by a 250+ clinician network and a strong multi-state
              presence across West Bengal, Assam, Bhubaneswar, Bihar, and Uttar
              Pradesh, we ensure accelerated enrollment, flawless compliance,
              and uncompromising data quality.
            </p>
            <button
              className="btn-primary"
              style={{ background: "#1B8FA6" }}
              onClick={() => goTo("/about")}
            >
              Learn More About Us →
            </button>
          </div>

          {/* Animated About Stats */}
          <div className="about-nums">
            {[
              [stats.trials, "Clinical Trials Completed"],
              [stats.years, "Company Age"],
              [stats.clinicians, "Clinician Network"],
              [stats.states, "States Covered"],
            ].map(([n, l], i) => (
              <AnimatedAboutNum key={i} value={n} label={l} />
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATIONS ── */}
      <section>
        <div className="sec-tag">Our Presence</div>
        <h2 className="sec-title">Pan-India Clinical Excellence</h2>
        <p
          style={{
            color: "#5E6E82",
            fontSize: 16,
            lineHeight: 1.75,
            maxWidth: 620,
            marginBottom: 36,
          }}
        >
          With Kolkata as our strategic hub, Accelia's presence spans across all
          regions of West Bengal and key locations including Assam, Bhubaneswar,
          Bihar, and Uttar Pradesh.
        </p>
        <div className="loc-grid">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="loc-card"
                style={{
                  gap: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Skeleton width={40} height={40} radius={999} />
                <Skeleton width="60%" height={16} />
                <Skeleton width="80%" height={13} />
              </div>
            ))
          ) : locations.length === 0 ? (
            <p style={{ color: "#5E6E82", fontSize: 14, gridColumn: "1/-1" }}>
              No locations found. Check console for API response shape.
            </p>
          ) : (
            locations.map((l, i) => (
              <div
                key={l._id || i}
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  overflow: "hidden",
                  boxShadow: "0 2px 16px rgba(11,31,58,0.08)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 32px rgba(11,31,58,0.14)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 16px rgba(11,31,58,0.08)";
                }}
              >
                {/* Image */}
                <div
                  style={{
                    width: "100%",
                    height: 180,
                    overflow: "hidden",
                    background: "#D8E4ED",
                  }}
                >
                  {l.image ? (
                    <img
                      src={l.image}
                      alt={l.city}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(135deg, #1B8FA6 0%, #0B1F3A 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 48,
                      }}
                    >
                      🏙️
                    </div>
                  )}
                </div>

                {/* Body */}
                <div style={{ padding: "18px 20px 22px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontSize: 26 }}>🔬</span>
                    <div>
                      <h3
                        style={{
                          fontFamily: "'Cormorant Garamond', serif",
                          fontSize: 22,
                          fontWeight: 700,
                          color: "#0B1F3A",
                          margin: 0,
                          lineHeight: 1.2,
                        }}
                      >
                        {l.city || l.name}
                      </h3>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          color: "#1B8FA6",
                          textTransform: "uppercase",
                          marginTop: 2,
                        }}
                      >
                        {l.state}
                      </div>
                    </div>
                  </div>
                  <p
                    style={{
                      color: "#5E6E82",
                      fontSize: 14,
                      margin: "8px 0 0",
                      lineHeight: 1.6,
                    }}
                  >
                    {l.tagline || l.note}
                  </p>
                  {l.hq && (
                    <div
                      style={{
                        marginTop: 12,
                        display: "inline-block",
                        background: "#0B1F3A",
                        color: "#C9A84C",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        padding: "4px 12px",
                        borderRadius: 999,
                      }}
                    >
                      HQ
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="cta-strip">
        <div>
          <h2>Ready to Partner With Us?</h2>
          <p>
            Email: acceliaclinicalsolution@gmail.com | Phone: +91 8282986162
          </p>
        </div>
        <button className="btn-primary" onClick={() => goTo("/contact")}>
          Contact Us →
        </button>
      </div>

      <Footer />

      <Modal
        modal={modal}
        onClose={() => setModal(null)}
        onContact={() => goTo("/contact")}
      />
    </>
  );
}
